import { doLinkResource } from 'actions/userIntegrations'
import { oauthTokenSelector } from 'selectors/app'
import { selectCurrentTicketId } from 'ducks/tickets/selectors/selectCurrentTicketId'

import * as linear from '../api'
import {
  CREATE_ISSUE_FAILURE,
  CREATE_ISSUE_REQUEST,
  CREATE_ISSUE_SUCCESS,
  FETCH_ISSUE_FAILURE,
  FETCH_ISSUE_REQUEST,
  FETCH_ISSUE_SUCCESS,
  FETCH_TEAMS_FAILURE,
  FETCH_TEAMS_REQUEST,
  FETCH_TEAMS_SUCCESS,
  FETCH_WORKFLOW_STATES_FAILURE,
  FETCH_WORKFLOW_STATES_REQUEST,
  FETCH_WORKFLOW_STATES_SUCCESS,
} from '../types'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const ISSUE_IDENTIFIER = /^[A-Z][A-Z0-9]{0,9}-[1-9][0-9]{0,9}$/
const ISSUE_URL_PATH = /^\/[^/]+\/issue\/([^/]+)(?:\/[^/?#]+)?\/?$/
const LINEAR_ISSUE_LINK_RECOVERY = 'linear_issue_link_recovery'
const LINEAR_ISSUE_LINK_ERROR = 'linear_issue_link_error'

const validIssueId = value =>
  typeof value === 'string' &&
  (UUID.test(value) || (value.length <= 21 && ISSUE_IDENTIFIER.test(value)))

export function normalizeIssueReference(reference) {
  const trimmed = (reference || '').trim()
  if (!trimmed) return null
  if (validIssueId(trimmed)) return { id: trimmed }
  try {
    const url = new URL(trimmed)
    if (
      url.protocol !== 'https:' ||
      url.hostname !== 'linear.app' ||
      !ISSUE_URL_PATH.test(url.pathname) ||
      url.search ||
      url.hash
    ) {
      return null
    }
    const id = url.pathname.match(ISSUE_URL_PATH)[1]
    return validIssueId(id) ? { id } : null
  } catch (_) {
    return null
  }
}

export function createLinearIssueLinkRecovery(issue, ticketId) {
  return {
    kind: LINEAR_ISSUE_LINK_RECOVERY,
    issue,
    ticketId,
  }
}

export function isLinearIssueLinkRecovery(result) {
  return result?.kind === LINEAR_ISSUE_LINK_RECOVERY
}

function createLinearIssueLinkError(action, details) {
  return {
    ...details,
    action,
    kind: LINEAR_ISSUE_LINK_ERROR,
  }
}

export const doFetchLinearTeams = () => async (dispatch, getState) => {
  dispatch({ type: FETCH_TEAMS_REQUEST })
  try {
    const teams = await linear.teams(oauthTokenSelector(getState()))
    return dispatch({ type: FETCH_TEAMS_SUCCESS, item: teams })
  } catch (error) {
    return dispatch({ type: FETCH_TEAMS_FAILURE, item: error })
  }
}

export const doFetchLinearWorkflowStates = teamId => async (
  dispatch,
  getState
) => {
  if (!teamId) return null
  dispatch({ type: FETCH_WORKFLOW_STATES_REQUEST, item: { teamId } })
  try {
    const workflowStates = await linear.workflowStates(
      oauthTokenSelector(getState()),
      teamId
    )
    return dispatch({
      type: FETCH_WORKFLOW_STATES_SUCCESS,
      item: { teamId, workflowStates },
    })
  } catch (error) {
    return dispatch({
      type: FETCH_WORKFLOW_STATES_FAILURE,
      item: { teamId, error },
    })
  }
}

export const doFetchLinearAssignees = teamId => async (_, getState) => {
  if (!teamId) return []
  return linear.assignees(oauthTokenSelector(getState()), teamId)
}

export const doFetchLinearLabels = teamId => async (_, getState) => {
  if (!teamId) return []
  return linear.labels(oauthTokenSelector(getState()), teamId)
}

export const doFetchLinearProjects = teamId => async (_, getState) => {
  if (!teamId) return []
  return linear.projects(oauthTokenSelector(getState()), teamId)
}

export const doSearchLinearIssues = term => async (_, getState) => {
  const trimmed = (term || '').trim()
  if (trimmed.length < 2) return []
  return linear.searchIssues(oauthTokenSelector(getState()), trimmed)
}

export const doFetchLinearIssue = reference => async (dispatch, getState) => {
  const normalizedReference = normalizeIssueReference(reference)
  if (!normalizedReference) return null
  dispatch({ type: FETCH_ISSUE_REQUEST, item: normalizedReference })
  try {
    const issue = await linear.issue(
      oauthTokenSelector(getState()),
      normalizedReference
    )
    dispatch({ type: FETCH_ISSUE_SUCCESS, item: issue })
    return issue
  } catch (error) {
    dispatch({ type: FETCH_ISSUE_FAILURE, item: error })
    throw error
  }
}

export const doFetchLinearIssues = references => async dispatch => {
  if (!references || references.length === 0) return []
  return Promise.all(
    references.map(reference =>
      dispatch(doFetchLinearIssue(reference)).catch(() => null)
    )
  )
}

export const doCreateAndLinkLinearIssueToTicket = ({
  teamId,
  workflowStateId,
  title,
  description,
  assigneeId,
  labelIds,
  projectId,
}) => async (dispatch, getState) => {
  const ticketId = selectCurrentTicketId(getState())
  dispatch({ type: CREATE_ISSUE_REQUEST })
  try {
    const variables = {
      teamId,
      title,
      stateId: workflowStateId,
    }
    if (description) variables.description = description
    if (assigneeId) variables.assigneeId = assigneeId
    if (projectId) variables.projectId = projectId
    if (labelIds && labelIds.length > 0) variables.labelIds = labelIds
    const issue = await linear.createIssue(
      oauthTokenSelector(getState()),
      variables
    )
    dispatch({ type: CREATE_ISSUE_SUCCESS, item: issue })
    try {
      const linkResult = await dispatch(
        doLinkResource({
          id: issue.id,
          provider: 'linear',
          ticketId,
          title: issue.title,
          url: issue.url,
        })
      )
      if (!linkResult) return createLinearIssueLinkRecovery(issue, ticketId)
    } catch (error) {
      return createLinearIssueLinkRecovery(issue, ticketId)
    }
    return issue
  } catch (error) {
    dispatch({ type: CREATE_ISSUE_FAILURE, item: error })
    throw error
  }
}

export const doLinkLinearIssueToTicket = reference => async (
  dispatch,
  getState
) => {
  const ticketId = selectCurrentTicketId(getState())
  const issue = await dispatch(doFetchLinearIssue(reference))
  if (!issue) return null
  const linkResult = await dispatch(
    doLinkResource({
      id: issue.id,
      provider: 'linear',
      ticketId,
      title: issue.title,
      url: issue.url,
    })
  )
  if (!linkResult) {
    throw createLinearIssueLinkError('existing_issue', {
      issueId: issue.id,
      message: 'Could not link existing Linear issue.',
      ticketId,
    })
  }
  return linkResult
}

export const doRetryLinearIssueLinkToTicket = recovery => async dispatch => {
  if (!recovery?.issue || !recovery.ticketId) return null
  const linkResult = await dispatch(
    doLinkResource({
      id: recovery.issue.id,
      provider: 'linear',
      ticketId: recovery.ticketId,
      title: recovery.issue.title,
      url: recovery.issue.url,
    })
  )
  if (!linkResult) {
    throw createLinearIssueLinkError('retry_issue', {
      issueId: recovery.issue.id,
      message: 'Could not link the created Linear issue.',
      ticketId: recovery.ticketId,
    })
  }
  return linkResult
}

export const doUnlinkLinearIssue = issue => dispatch => {
  if (!issue) return null
  return dispatch(doLinkResource(linearUnlinkResource(issue)))
}

export function linearUnlinkResource(issue) {
  return {
    id: issue.id || issue.externalId,
    provider: 'linear',
    removed: true,
    title: issue.title,
    url: issue.url,
  }
}
